import { getRootFolderIdForGivenContext } from './moveToCommonUtils';
import { rootFolderString } from './moveToStringUtils.locstring.json';
import MoveToMailboxContext from '../store/schema/MoveToMailboxContext';
import { getStore as getMoveToStore } from '../store/store';
import { ObservableMap } from 'mobx';
import folderStore, {
    Folder,
    getEffectiveFolderDisplayName,
    getMailRootFolderChildIds,
    getPrimaryMailboxRootFolder,
    shouldDisplayFolderInFolderTree,
} from 'owa-folders';
import loc from 'owa-localize';
import { getStore } from 'owa-mail-move-mru/lib/store/store';
import { MoveToFoldersType, MoveToFolderType } from 'owa-mail-moveto-control';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { select } from 'satcheljs/lib/legacy';

export const DEFAULT_MAX_FOLDERS = 8;

function sortByDisplayName(array: Array<MoveToFolderType>) {
    array.sort((folder1: MoveToFolderType, folder2: MoveToFolderType) => {
        return folder1.displayName.localeCompare(folder2.displayName);
    });
}

function sortByPrefixMatch(array: Array<MoveToFolderType>, searchText: string) {
    array.sort((folder1: MoveToFolderType, folder2: MoveToFolderType) => {
        const folder1Name = folder1.displayName.toLocaleLowerCase();
        const folder2Name = folder2.displayName.toLocaleLowerCase();
        const folder1IsPrefixMatch = folder1Name.indexOf(searchText) == 0;
        const folder2IsPrefixMatch = folder2Name.indexOf(searchText) == 0;

        // Sort array so that folders with prefix match shows first
        if (folder1IsPrefixMatch && !folder2IsPrefixMatch) {
            return -1;
        } else if (!folder1IsPrefixMatch && folder2IsPrefixMatch) {
            return 1;
        } else {
            return 0;
        }
    });
}

function unique(array: Array<any>): Array<any> {
    const map = {};

    for (var i = 0, len = array.length; i < len; i++) {
        const value = array[i];

        if (value) {
            // We assign 0 just to store something. We only care about the keys anyway.
            map[value] = 0;
        }
    }

    return Object.keys(map);
}

/**
 * Gets folder ids from current moveTo mailbox for searched text
 * @param moveToMailboxContext - moveTo mailbox context for search
 * @param search - search text
 * @param folderTable - folder table from folder store
 * @return - folder ids
 */
function getFolderIdsForSearch(
    moveToMailboxContext: MoveToMailboxContext,
    search: string,
    folderTable: ObservableMap<string, Folder>
): string[] {
    const allRootFolderIds = getMailRootFolderChildIds(
        moveToMailboxContext,
        getMoveToStore().moveToSharedFolderRootId
    );
    const folderIds: string[] = [];
    let folderId;
    while ((folderId = allRootFolderIds.pop())) {
        const folder = folderTable.get(folderId);

        // non-mail folders will be shown in search results without check (e.g. calendar folder)
        if (folder && shouldDisplayFolderInFolderTree(folder)) {
            const folderName = getEffectiveFolderDisplayName(folder).toLocaleLowerCase();
            if (folderName.indexOf(search) >= 0) {
                folderIds.push(folderId);
            }

            // Append child folderIds to the queue.
            if (folder.childFolderIds) {
                for (const childFolderId of folder.childFolderIds) {
                    allRootFolderIds.push(childFolderId);
                }
            }
        }
    }

    return folderIds;
}

/**
 * Gets all folders for given moveTo mailbox context
 * @param moveToMailboxContext - moveTo mailbox context
 * @param shouldShowRootFolder - flag indicating whether to show root folder in all folder menu
 * @return - all folders of the mailbox contained in interface MoveToFoldersType
 */
export function getAllFoldersForMoveTo(
    moveToMailboxContext: MoveToMailboxContext,
    shouldShowRootFolder?: boolean
): MoveToFoldersType {
    const allFoldersList = [];
    const rootFolderChildren = getMailRootFolderChildIds(
        moveToMailboxContext,
        getMoveToStore().moveToSharedFolderRootId
    );
    let depthValue = 0;
    if (shouldShowRootFolder && moveToMailboxContext === 'primary') {
        const rootFolder = getPrimaryMailboxRootFolder();
        allFoldersList.push({
            folderId: rootFolder.FolderId.Id,
            displayName: loc(rootFolderString),
            parentFolderId: rootFolder.ParentFolderId.Id,
            parentFolderDisplayName: null,
            depth: depthValue++,
        });
    }
    addAllChildFoldersToFolderList(allFoldersList, rootFolderChildren, depthValue);
    return { folders: allFoldersList, hasExactMatch: false };
}

// Add child folders
function addAllChildFoldersToFolderList(
    allFoldersList: MoveToFolderType[],
    childFolders: string[],
    depth: number
) {
    const folderTable = folderStore.folderTable;

    for (const childFolderId of childFolders) {
        const childFolder = folderTable.get(childFolderId);

        // Don't add hidden folders to list and don't show move Notes folder in move to option
        if (
            shouldDisplayFolderInFolderTree(childFolder) &&
            folderIdToName(childFolderId) != 'notes'
        ) {
            const folder = folderTable.get(childFolder.FolderId.Id);
            const parentFolderId = folder.ParentFolderId.Id;
            allFoldersList.push({
                folderId: folder.FolderId.Id,
                displayName: getEffectiveFolderDisplayName(folder),
                parentFolderId: parentFolderId,
                parentFolderDisplayName: parentFolderId
                    ? getEffectiveFolderDisplayName(folderTable.get(parentFolderId))
                    : null,
                depth: depth,
            });

            if (childFolder.childFolderIds) {
                addAllChildFoldersToFolderList(
                    allFoldersList,
                    childFolder.childFolderIds,
                    depth + 1
                );
            }
        }
    }
}

export interface GetFoldersForMoveToState {
    folderTable: ObservableMap<string, Folder>;
}

/**
 * get folders for move to menu
 * @param moveToMailboxContext - moveTo mailbox context
 * @param search - searched text
 * @param maxItems - maximum number of folders that should be returned
 * @param customFilter - function defining custom filter for folders found
 * @param disableSelectedFolder - flag to determine whether to hide the current selected folder
 * @param selectedFolderId - the selected folder id
 * @param state - folderStore
 * @return - folders found contained in MoveToFoldersType interface
 */
const getFoldersForMoveTo = select({
    folderTable: () => folderStore.folderTable,
})(function getFoldersForMoveTo(
    moveToMailboxContext: MoveToMailboxContext,
    depth: number,
    search?: string,
    maxItems?: number,
    customFilter?: (folderId: string) => boolean,
    disableSelectedFolder?: boolean,
    selectedFolderId?: string,
    state?: GetFoldersForMoveToState
): MoveToFoldersType {
    search = (search || '').trim().toLocaleLowerCase();
    maxItems = maxItems || DEFAULT_MAX_FOLDERS;
    let hasExactMatch = false;
    const isSearchTextPresent = search.length > 0;
    let folderIds = isSearchTextPresent
        ? getFolderIdsForSearch(moveToMailboxContext, search, state.folderTable)
        : getStore().mruFolders;

    if (customFilter) {
        folderIds = folderIds.filter(customFilter);
    }

    folderIds = unique(folderIds);

    const folders: MoveToFolderType[] = [];
    const rootFolderIdForGivenContext =
        moveToMailboxContext === 'sharedFolder'
            ? getMoveToStore().moveToSharedFolderRootId
            : getRootFolderIdForGivenContext(moveToMailboxContext);
    for (const folderId of folderIds) {
        const folder = state.folderTable.get(folderId);

        if (folder) {
            // Check to skip notes folder option in move to menu
            if (folderIdToName(folder.FolderId.Id) === 'notes') {
                continue;
            }

            if (!isSearchTextPresent && disableSelectedFolder && folderId === selectedFolderId) {
                continue;
            }

            const folderName = getEffectiveFolderDisplayName(folder);

            // Parent folder Id can be null if folder is root, and parent folder is not necessarily in
            // the folder table (mru can be out of sync because of desktop deletion)
            const parentFolderId = folder.ParentFolderId ? folder.ParentFolderId.Id : null;
            const parentFolder = parentFolderId ? state.folderTable.get(parentFolderId) : null;

            // We have an exact match if the search string is non-empty and it matches a folder
            // at the root of the current mailbox context
            if (
                isSearchTextPresent &&
                parentFolderId == rootFolderIdForGivenContext &&
                folderName.toLocaleLowerCase() == search
            ) {
                hasExactMatch = true;
            }

            folders.push({
                folderId: folder.FolderId.Id,
                displayName: folderName,
                parentFolderId: parentFolderId,
                parentFolderDisplayName: parentFolder
                    ? getEffectiveFolderDisplayName(parentFolder)
                    : null,
                depth: depth,
            });
        }
    }

    if (search) {
        sortByDisplayName(folders);
        sortByPrefixMatch(folders, search);
    }

    return {
        folders: folders.slice(0, maxItems),
        hasExactMatch: hasExactMatch,
    };
});

/**
 * get folders for a given mailbox filtered by search text
 * @param moveToMailboxContext - moveTo mailbox context
 * @param findText - searched text
 * @param shouldShowAllFolders - flag indicating whether to show all folders
 * @param shouldShowRootFolder - flag indicating whether to show root folder in all folder menu
 * @param disableSelectedFolder - flag to determine whether to hide the current selected folder
 * @param selectedFolderId - the selected folder id
 * @return - folders with MoveToFoldersType interface
 */
export function getFoldersForGivenContext(
    moveToMailboxContext: MoveToMailboxContext,
    findText: string,
    shouldShowAllFolders: boolean,
    shouldShowRootFolder?: boolean,
    disableSelectedFolder?: boolean,
    selectedFolderId?: string
): MoveToFoldersType {
    // show all folders
    if (shouldShowAllFolders && !findText) {
        return getAllFoldersForMoveTo(moveToMailboxContext, shouldShowRootFolder);
    } else {
        // show filtered list of folders of selected context
        return getFoldersForMoveTo(
            moveToMailboxContext,
            0 /* depth */,
            findText,
            DEFAULT_MAX_FOLDERS,
            null /* customFilter */,
            disableSelectedFolder,
            selectedFolderId
        );
    }
}

export default getFoldersForMoveTo;
